<script setup lang="ts">
// The AI Recommendation dashboard: gauge, confidence, cost ranges, lifespan,
// recommended action, next steps, red flags.
import { computed } from 'vue'
import type { DiagnosisResponse } from '@/shared/api/types'
import GlassCard from '@/shared/components/GlassCard.vue'
import RepairReplaceGauge from '../components/RepairReplaceGauge.vue'
import CostComparisonChart from '../components/CostComparisonChart.vue'
import ConfidenceBadge from '../components/ConfidenceBadge.vue'

const props = defineProps<{ response: DiagnosisResponse }>()

const result = computed(() => props.response.result)

const ACTION_LABELS: Record<string, string> = {
    repair: 'Repair looks like the smart move',
    replace: 'Replacement looks like the smart move',
    either: 'Genuinely close call — either path is defensible',
    professional_evaluation: 'Get a professional evaluation first',
}

const usd = (n: number) => `$${n.toLocaleString()}`
</script>

<template>
    <section id="results" class="site-section">
        <div class="site-content">
            <h2>Your AI Analysis</h2>
            <p class="muted">
                Based only on what you told us — an on-site inspection can confirm it.
                <ConfidenceBadge :confidence="result.confidence" />
            </p>

            <div class="dashboard">
                <GlassCard class="cell gauge-cell">
                    <h3>Repair vs Replace</h3>
                    <RepairReplaceGauge :repair-score="result.repair_score" />
                    <p class="action">{{ ACTION_LABELS[result.recommended_action] ?? result.recommended_action }}</p>
                </GlassCard>

                <GlassCard class="cell">
                    <h3>Estimated Costs</h3>
                    <CostComparisonChart
                        :repair="result.estimated_repair_cost"
                        :replacement="result.estimated_replacement_cost"
                    />
                    <ul class="facts">
                        <li>
                            <span class="muted">Energy savings if replaced</span>
                            <strong>~{{ usd(result.estimated_annual_energy_savings_usd) }}/yr</strong>
                        </li>
                        <li>
                            <span class="muted">Estimated life remaining</span>
                            <strong>{{ result.remaining_life_years.low }}–{{ result.remaining_life_years.high }} years</strong>
                        </li>
                    </ul>
                </GlassCard>

                <GlassCard class="cell wide">
                    <h3>What's likely going on</h3>
                    <p>{{ result.diagnosis }}</p>
                    <ul class="causes">
                        <li v-for="cause in result.likely_causes" :key="cause.cause">
                            <span class="badge likelihood" :data-level="cause.likelihood">{{ cause.likelihood }}</span>
                            {{ cause.cause }}
                        </li>
                    </ul>
                    <p class="summary muted">{{ result.summary }}</p>
                </GlassCard>

                <GlassCard v-if="result.red_flags.length" class="cell wide danger">
                    <h3>⚠ Safety first</h3>
                    <ul>
                        <li v-for="flag in result.red_flags" :key="flag">{{ flag }}</li>
                    </ul>
                </GlassCard>

                <GlassCard class="cell wide">
                    <h3>Suggested next steps</h3>
                    <ol class="steps">
                        <li v-for="step in result.next_steps" :key="step">{{ step }}</li>
                    </ol>
                    <p class="muted small">
                        AI interactions used: {{ response.ai_used }} of {{ response.ai_limit }}.
                    </p>
                </GlassCard>
            </div>
        </div>
    </section>
</template>

<style scoped lang="scss">
.dashboard {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: var(--site-space-lg);
    margin-top: var(--site-space-lg);
}

.cell {
    h3 {
        margin-bottom: var(--site-space-md);
    }

    &.wide {
        grid-column: 1 / -1;
    }

    &.danger {
        border-color: var(--site-danger);
    }
}

.action {
    text-align: center;
    font-weight: 700;
    margin: var(--site-space-sm) 0 0;
}

.facts {
    list-style: none;
    padding: 0;
    margin: var(--site-space-md) 0 0;

    li {
        display: flex;
        justify-content: space-between;
        gap: var(--site-space-md);
        padding: var(--site-space-xs) 0;
        border-top: 1px dashed var(--site-border);
    }
}

.causes {
    list-style: none;
    padding: 0;
    margin: var(--site-space-sm) 0;

    li {
        padding: var(--site-space-xs) 0;
    }
}

.likelihood {
    margin-right: var(--site-space-sm);
    text-transform: capitalize;

    &[data-level='high'] {
        background: var(--site-accent-soft);
    }

    &[data-level='medium'] {
        background: transparent;
        border: 1px solid var(--site-border);
        color: var(--site-text-muted);
    }

    &[data-level='low'] {
        background: transparent;
        border: 1px dashed var(--site-border);
        color: var(--site-text-muted);
    }
}

.summary {
    margin-top: var(--site-space-md);
}

.steps li {
    margin-bottom: var(--site-space-xs);
}

.small {
    font-size: 0.85rem;
    margin-top: var(--site-space-md);
}
</style>
